import React from 'react';
import { X } from 'lucide-react';
import { Video } from './VideoGallery';
import '../styles/VideoModal.css';

interface VideoModalProps {
  video: Video;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose }) => {
  const getEmbedUrl = (): string => {
    if (video.type === 'youtube') {
      // Support multiple YouTube URL formats
      const patterns = [
        /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
        /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
        /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
        /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/
      ];

      for (const pattern of patterns) {
        const match = video.url.match(pattern);
        if (match) {
          // Add origin parameter and enable js api for better compatibility
          const origin = window.location.origin;
          return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0&modestbranding=1&origin=${encodeURIComponent(origin)}&enablejsapi=1`;
        }
      }
    }

    if (video.type === 'vimeo') {
      const match = video.url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1&byline=0&portrait=0`;
      }
    }

    if (video.type === 'mp4') {
      return video.url;
    }

    return video.url;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="video-player-overlay" onClick={handleBackdropClick}>
      <div className="video-player-modal">
        <div className="video-player-header">
          <h3 className="video-player-title">{video.title}</h3>
          <button className="video-player-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="video-player-container">
          {video.type === 'mp4' ? (
            <video
              controls
              autoPlay
              className="video-player-element"
            >
              <source src={getEmbedUrl()} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <iframe
              src={getEmbedUrl()}
              className="video-player-iframe"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              title={video.title}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
